import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import Hidden, { HiddenProps } from "@material-ui/core/Hidden";
import {
  Home,
  MessageSquare,
  Heart,
  ShieldCheck,
  MessageCircleX,
  CreditCard,
  LogOut,
  UserPlus,
  Settings,
  CircleDollarSign,
  Megaphone,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../redux/typedHooks";
import { UserInterface } from "../../../types/user";
import { useSideBarStyles } from "./style";
import ChartIcon from "../../../assets/ChartIconHelp";
import DiscordIcon from "../../../assets/DiscordIcon";
import plusIconSidebar from "../../../assets/svgs/plusIconSidebar.svg";
import { SidebarFreeMessagesIntegration } from "../../elements/FreeMessagesIntegration";
import Logout from "../../../utils/logout";
import { getUserType } from "../../../utils/getUserType";
import clsx from "clsx";

const FixedHidden = Hidden as React.ComponentType<
  HiddenProps & { children?: React.ReactNode }
>;

type SidebarProps = {
  mobileOpen: boolean;
  onToggle: () => void;
};

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onToggle }) => {
  const classes = useSideBarStyles();
  const location = useLocation();
  const [isLogout, setIsLogout] = useState(false);

  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );
  const normalizedUserType = getUserType(user)?.title?.toLowerCase();
  const isViewer = normalizedUserType === "viewer";
  const isCreator =
    normalizedUserType === "model" || normalizedUserType === "creator";
  const icons = [
    ...(isCreator
      ? [
        {
          title: "New post",
          icon: (
            <img
              src={plusIconSidebar}
              alt=""
              style={{ width: 24, height: 24, display: "block" }}
            />
          ),
          to: "/post-creator",
        },
      ]
      : []),
    { title: "Home", icon: <Home size={24} />, to: "/" },
    {
      title: "Messages",
      icon: <MessageSquare size={24} />,
      to: "/messages",
    },
    ...(!isViewer
      ? [
        {
          title: "Earnings and Analytics",
          icon: <ChartIcon width={24} height={24} />,
          to: "/dashboard",
        },
      ]
      : []),
    {
      title: "Favorites",
      icon: <Heart size={24} />,
      to: "/liked-posts",
    },
    {
      title: "Referral Program",
      icon: <UserPlus size={24} />,
      to: "/referral",
    },
  ];

  if (user?.isAdmin) {
    icons.push({
      title: "Admin Payouts",
      icon: <CircleDollarSign />,
      to: "/admin-payouts",
    });
  }

  if (user?.isAdmin) {
    icons.push({
      title: "Age Proofs",
      icon: <ShieldCheck size={24} />,
      to: "/admin/age-proofs",
    });
    icons.push({
      title: "Content Moderation",
      icon: <MessageCircleX size={24} />,
      to: "/admin/moderation",
    });
    icons.push({
      title: "Transactions",
      icon: <CreditCard size={24} />,
      to: "/admin/transactions",
    });
    icons.push({
      title: "Update Center",
      icon: <Megaphone size={24} />,
      to: "/admin/update-center",
    });
  }

  const handleItemClick = () => {
    if (mobileOpen) {
      onToggle();
    }
  };

  const handleLogout = () => {
    setIsLogout(true);
    if (mobileOpen) {
      onToggle();
    }
  };

  const desktopContent = (
    <Box className={classes.sidebar}>
      <Box className={classes.sidebarIconsTop}>
        {icons.map(({ title, icon, to }) => {
          const isActive =
            location.pathname === to ||
            (to !== "/" && location.pathname.startsWith(to));
          return (
            <Tooltip key={title} title={title} placement="right">
              <IconButton
                className={
                  isActive ? classes.sidebarIconActive : classes.sidebarIcon
                }
                component={Link}
                to={to}
              >
                {icon}
              </IconButton>
            </Tooltip>
          );
        })}

        <SidebarFreeMessagesIntegration />
      </Box>

      <Box className={classes.sidebarIconsBottom}>
        <Tooltip title="Discord" placement="right">
          <IconButton
            className={classes.sidebarIcon}
            component="a"
            href="https://discord.gg/RKJXnUKVk4"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DiscordIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // Create separate array for mobile menu (without Post Creator, with Settings)
  const mobileIcons = icons.concat([
    {
      title: "Settings",
      icon: <Settings size={24} />,
      to: "/settings",
    },
  ]);

  const mobileContent = (
    <Box className={classes.mobileMenuContainer}>
      <Box className={classes.mobileMenuScrollable}>
        <List className={classes.mobileMenuList}>
          {mobileIcons.map(({ title, icon, to }) => {
            const isActive =
              location.pathname === to ||
              (to !== "/" && location.pathname.startsWith(to));
            return (
              <ListItem
                button
                key={title}
                component={Link}
                to={to}
                onClick={handleItemClick}
                className={clsx(classes.mobileMenuItem, {
                  [classes.mobileMenuItemActive]: isActive,
                })}
              >
                <ListItemIcon className={classes.mobileListIcon}>
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={title}
                  classes={{ primary: classes.mobileMenuText }}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box className={classes.mobileBottom}>
        <Divider className={classes.mobileMenuDivider} />
        <ListItem
          button
          onClick={handleLogout}
          className={classes.mobileMenuItem}
        >
          <ListItemIcon className={classes.mobileListIcon}>
            <LogOut size={24} />
          </ListItemIcon>
          <ListItemText
            primary="Log out"
            classes={{ primary: classes.mobileMenuText }}
          />
        </ListItem>
      </Box>
    </Box>
  );

  if (isLogout) {
    return <Logout />;
  }

  return (
    <>
      <FixedHidden mdUp implementation="css">
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={onToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
          BackdropProps={{ invisible: true }}
        >
          {mobileContent}
        </Drawer>
      </FixedHidden>
      <FixedHidden smDown implementation="css">
        <>{desktopContent}</>
      </FixedHidden>
    </>
  );
};

export default Sidebar;
